/**
 * Decode Google polyline encoded string to coordinates
 * @param {string} encoded - Encoded polyline string
 * @returns {Array} Array of [lat, lon] coordinates
 */
function decodePolyline(encoded) {
  const coords = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    coords.push([lat / 1e5, lng / 1e5]);
  }

  return coords;
}

/**
 * Replay Manager - Handles loading and playback of historical flight data
 * Supports both JSON (full telemetry) and polyline (compact) formats
 */
import { cookieRegistry } from '../cookieRegistry.js';
import { positionNoiseFilter } from '../positionNoiseFilter.js';

export class ReplayManager {
  constructor(aircraftManager, mapManager, httpUrl, aircraftStateTracker = null, polarManager = null, energyCalculator = null) {
    this.aircraftManager = aircraftManager;
    this.mapManager = mapManager;
    this.httpUrl = httpUrl; // Backend HTTP URL
    this.aircraftStateTracker = aircraftStateTracker; // Phase 2: State tracker for start/finish detection

    // Phase 4: Polar and Energy calculators
    this.polarManager = polarManager;
    this.energyCalculator = energyCalculator;

    // Replay state
    this.flights = [];
    this.isPlaying = false;
    this.isPaused = false;
    this.playbackSpeed = 1.0;
    this.currentTime = null;
    this.minTime = null;
    this.maxTime = null;
    this.animationFrameId = null;
    this.lastFrameTime = null;

    // Task state for start/finish detection
    this.selectedTask = null;

    // Performance
    this.updateInterval = 100; // Update every 100ms for smooth animation

    // Stats/leaderboard refresh rate - now controlled by config.replayRefreshHz via the slider.
    // These fields are no longer used here; throttle is in main.js updateDistanceStatsWidget().
    this._lastStatsCallTime = 0;

    // Live-append (PVR) mode state
    this.isLiveMode = false;
    this.liveEdge = null;               // Date: latest live position timestamp
    this.cookieToFlightIndex = new Map(); // canonical cookie -> index in this.flights[]
    this._pendingEventRecompute = new Set(); // cookies needing event recompute
    this._lastEventRecomputeTime = 0;

    // Align Starts state (virtual time translation for replay comparison)
    this.alignStartsEnabled = false;
    this.flightAlignOffsets = new Map(); // flightIndex -> offset in ms
    this.alignedMinTime = null;
    this.alignedMaxTime = null;
    this.referenceStartTime = null; // earliest detected start (anchor point)

    // Worker-based timer for background-tab playback
    // (requestAnimationFrame is paused entirely in background tabs)
    this._bgWorker = null;
    this._bgWorkerUrl = null;
    try {
      const workerCode = 'let tid=null;self.onmessage=e=>{if(e.data==="start"){if(tid)clearInterval(tid);tid=setInterval(()=>self.postMessage("tick"),100)}else if(e.data==="stop"){if(tid){clearInterval(tid);tid=null}}}';
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      this._bgWorkerUrl = URL.createObjectURL(blob);
      this._bgWorker = new Worker(this._bgWorkerUrl);
      this._bgWorker.onmessage = () => {
        if (document.hidden && this.isPlaying) {
          this.animate();
        }
      };
    } catch (_) { /* Workers not available */ }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isPlaying) {
        this.ensurePlaybackLoop();
      }
    });
  }

  shouldRebuildStateFromHistory(task = this.selectedTask) {
    return false;
  }

  /**
   * Enable or disable start-time alignment for replay comparison.
   * When enabled, all flights are virtually shifted so their detected starts align.
   */
  setAlignStarts(enabled) {
    if (this.isLiveMode) return; // Not applicable in live mode

    this.alignStartsEnabled = !!enabled;
    this.flightAlignOffsets.clear();
    this.alignedMinTime = null;
    this.alignedMaxTime = null;
    this.referenceStartTime = null;

    if (!this.alignStartsEnabled || !this.aircraftStateTracker) {
      // Switching off — clear trails and let everything revert to original times
      if (this.mapManager && typeof this.mapManager.clearAllTrails === 'function') {
        this.mapManager.clearAllTrails();
      }
      return;
    }

    // Collect start times from precomputed events
    const startEntries = []; // { flightIndex, startMs }
    this.flights.forEach((flight, idx) => {
      const cookie = flight.positions?.[0]?.cookie;
      if (!cookie) return;
      const events = this.aircraftStateTracker.precomputedEvents?.get(cookie);
      if (events?.startTime) {
        startEntries.push({ idx, startMs: events.startTime.getTime() });
      }
    });

    if (startEntries.length === 0) {
      this.alignStartsEnabled = false;
      return;
    }

    // Reference = earliest start time (all flights align to this point)
    const earliestStartMs = Math.min(...startEntries.map(s => s.startMs));
    this.referenceStartTime = new Date(earliestStartMs);

    // Per-flight offset: ADD this to virtual currentTime to get real flight time
    startEntries.forEach(({ idx, startMs }) => {
      this.flightAlignOffsets.set(idx, startMs - earliestStartMs);
    });

    // Compute aligned timeline bounds
    let maxPreStartMs = 0;
    let maxPostStartMs = 0;
    this.flights.forEach((flight, idx) => {
      const offset = this.flightAlignOffsets.get(idx);
      if (offset === undefined) return;
      const cookie = flight.positions?.[0]?.cookie;
      const events = this.aircraftStateTracker.precomputedEvents?.get(cookie);
      if (!events?.startTime) return;
      const flightStartMs = events.startTime.getTime();
      const preStart = flightStartMs - flight.startTime.getTime();
      const postStart = flight.endTime.getTime() - flightStartMs;
      maxPreStartMs = Math.max(maxPreStartMs, preStart);
      maxPostStartMs = Math.max(maxPostStartMs, postStart);
    });

    this.alignedMinTime = new Date(earliestStartMs - maxPreStartMs);
    this.alignedMaxTime = new Date(earliestStartMs + maxPostStartMs);

    // Clamp current time to new bounds
    if (this.currentTime) {
      const effMin = this.getEffectiveMinTime();
      const effMax = this.getEffectiveMaxTime();
      if (this.currentTime < effMin) this.currentTime = new Date(effMin);
      if (this.currentTime > effMax) this.currentTime = new Date(effMax);
    }

    // Clear trails since positions have shifted
    if (this.mapManager && typeof this.mapManager.clearAllTrails === 'function') {
      this.mapManager.clearAllTrails();
    }
    this.aircraftManager.clearAll();

    // Reset cursors to rebuild from new time perspective
    this.flights.forEach(flight => {
      flight.currentIndex = 0;
      flight.lastRenderedIndex = -1;
    });

    // Rebuild state at current time
    this.rebuildAircraftStates(this.currentTime);
    this.updatePositions();
  }

  /**
   * Reset alignment state without side effects (no trail clearing, no rebuild).
   * Called before loading new data to ensure clean state.
   */
  resetAlignStarts() {
    this.alignStartsEnabled = false;
    this.flightAlignOffsets.clear();
    this.alignedMinTime = null;
    this.alignedMaxTime = null;
    this.referenceStartTime = null;
  }

  /**
   * Get the real flight time for a specific flight at the current virtual time.
   * When alignment is off, returns currentTime unchanged.
   */
  getFlightTime(flightIndex) {
    if (!this.alignStartsEnabled) return this.currentTime;
    const offset = this.flightAlignOffsets.get(flightIndex);
    if (offset === undefined) return this.currentTime;
    return new Date(this.currentTime.getTime() + offset);
  }

  /** Effective min time (aligned or original) */
  getEffectiveMinTime() {
    return (this.alignStartsEnabled && this.alignedMinTime) ? this.alignedMinTime : this.minTime;
  }

  /** Effective max time (aligned or original) */
  getEffectiveMaxTime() {
    return (this.alignStartsEnabled && this.alignedMaxTime) ? this.alignedMaxTime : this.maxTime;
  }

  rebuildAircraftStates(targetTime) {
    if (!this.aircraftStateTracker || !this.selectedTask || this.flights.length === 0) {
      return;
    }

    if (this.alignStartsEnabled) {
      // Per-flight reconstruction with offset times
      for (let i = 0; i < this.flights.length; i++) {
        const flight = this.flights[i];
        const cookie = flight.positions?.[0]?.cookie;
        if (!cookie || !flight.positions) continue;
        const effectiveTime = this.getFlightTime(i);
        const position = this.findLatestPositionAtOrBefore(flight, effectiveTime);
        this.aircraftStateTracker.reconstructStateFromEvents(cookie, effectiveTime, this.selectedTask, position);
      }
      return;
    }

    this.aircraftStateTracker.reconstructAllStatesFromEvents(
      this.flights,
      targetTime,
      this.selectedTask
    );
  }

  /**
   * Find the latest position in a flight at or before the given time.
   */
  findLatestPositionAtOrBefore(flight, targetTime) {
    const timestamps = flight.timestamps;
    const positions = flight.positions;
    if (!timestamps || timestamps.length === 0) return null;
    const targetMs = targetTime.getTime();
    // Binary search for efficiency
    let lo = 0, hi = timestamps.length - 1;
    if (timestamps[0].getTime() > targetMs) return null;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (timestamps[mid].getTime() <= targetMs) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return positions[lo] || null;
  }

  /**
   * Set the selected task for start/finish detection
   * Triggers precomputation of task events for all loaded flights
   * @param {object} task - Task object with turnpoints
   */
  setSelectedTask(task) {
    this.selectedTask = task;
    // DEBUG:     console.log(`🎬 ReplayManager: Task set for start/finish detection: ${task?.task_name}`);

    // Precompute all task events now that we have both flights and task
    if (this.aircraftStateTracker && this.selectedTask && this.flights.length > 0) {
      const maxStartAltitude_m = this.selectedTask.max_start_altitude_m || 999999;
      // DEBUG:       console.log(`📊 Precomputing events for ${this.flights.length} flights with task: ${task.task_name}`);
      this.aircraftStateTracker.precomputeAllEvents(this.flights, this.selectedTask, maxStartAltitude_m);

      // Debug: summarize precomputed events
      let startsFound = 0, finishesFound = 0, noCandidates = 0;
      this.aircraftStateTracker.precomputedEvents.forEach((events, cookie) => {
        if (events.startTime) startsFound++;
        if (events.finishTime) finishesFound++;
        if (events.startCandidates.length === 0) noCandidates++;
      });
      const refStartMs = this.aircraftStateTracker.getRegattaReferenceStartTime();
      console.log(`[Precompute] ${this.flights.length} flights → ${startsFound} starts, ${finishesFound} finishes, ${noCandidates} with zero start candidates. RegattaRef=${refStartMs ? new Date(refStartMs).toISOString() : 'null'}, regatta_start=${task?.regatta_start}`);

      // Reconstruct state for current time
      if (this.currentTime) {
        this.rebuildAircraftStates(this.currentTime);
        this.updatePositions();
      }
    }
  }

  /**
   * Load replay data from API
   * @param {number} taskId - Task ID to load
   * @param {object} timeRange - Optional time range {start, end}
   * @param {string} format - Data format: 'json' or 'polyline' (default: 'json')
   */
  async load(taskId, timeRange = null, format = 'json') {
    this.resetAlignStarts();

    try {
      // DEBUG:       console.log('🎬 ReplayManager: Loading replay data...', { taskId, timeRange, format });

      // Build API URL with selected format
      let url = `${this.httpUrl}/api/positions/time-window?format=${format}`;

      if (taskId) {
        url += `&taskId=${taskId}`;
      } else if (timeRange) {
        url += `&startTime=${timeRange.start.toISOString()}&endTime=${timeRange.end.toISOString()}`;
      } else {
        throw new Error('Must provide either taskId or timeRange');
      }

      // DEBUG:       console.log('🎬 Fetching from:', url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error('API returned error');
      }

      // DEBUG:       console.log(`✅ Loaded ${data.total_flights} flights with ${data.total_positions} positions`);

      // DEBUG: Store in window for inspection
      window.DEBUG_REPLAY_DATA = data;
      // DEBUG:       console.log('🔍 DEBUG: Full response stored in window.DEBUG_REPLAY_DATA');
      // DEBUG:       console.log('🔍 To inspect: window.DEBUG_REPLAY_DATA.flights[0].positions[0]');

      // DEBUG: Log response structure
      // console.log('🔍 DEBUG: API Response structure:', {
      //   flights_count: data.flights?.length,
      //   first_flight_keys: data.flights?.[0] ? Object.keys(data.flights[0]) : 'no flights',
      //   first_flight_positions_count: data.flights?.[0]?.positions?.length,
      //   first_position_sample: data.flights?.[0]?.positions?.[0]
      // });

      // Process flights with format info
      const allFlights = data.flights.map(flight => this.processFlight(flight, format));

      // Deduplicate flights by cookie (same pilot may have multiple sessions).
      // Keep the flight with the most positions for each canonical cookie.
      const bestByCookie = new Map();
      for (const flight of allFlights) {
        const cookie = flight.positions?.[0]?.cookie;
        if (!cookie) continue;
        const existing = bestByCookie.get(cookie);
        if (!existing || flight.positions.length > existing.positions.length) {
          bestByCookie.set(cookie, flight);
        }
      }
      this.flights = Array.from(bestByCookie.values());

      // Calculate actual time range from flight data (not task window)
      // Find earliest start and latest end from all flights
      let actualMinTime = null;
      let actualMaxTime = null;

      this.flights.forEach(flight => {
        if (flight.timestamps && flight.timestamps.length > 0) {
          const flightStart = flight.timestamps[0];
          const flightEnd = flight.timestamps[flight.timestamps.length - 1];

          if (!actualMinTime || flightStart < actualMinTime) {
            actualMinTime = flightStart;
          }
          if (!actualMaxTime || flightEnd > actualMaxTime) {
            actualMaxTime = flightEnd;
          }
        }
      });

      // Use actual flight data range, or fall back to task window if no data
      this.minTime = actualMinTime || new Date(data.time_window.start);
      this.maxTime = actualMaxTime || new Date(data.time_window.end);
      this.currentTime = new Date(this.minTime);

      // DEBUG:       console.log(`🎬 Time range: ${this.minTime.toISOString()} to ${this.maxTime.toISOString()} (${((this.maxTime - this.minTime) / 1000 / 60).toFixed(1)} minutes)`);

      // Clear existing aircraft from live mode
      this.aircraftManager.clearAll();

      // Draw all flight paths (faded)
      this.drawFlightPaths();

      // Fit map to all tracks
      this.fitMapToTracks();

      // Update positions to show all active flights at start time
      // Note: Task event precomputation happens later in setSelectedTask() when task is loaded
      this.updatePositions();

      return {
        totalFlights: data.total_flights,
        totalPositions: data.total_positions,
        duration: (this.maxTime - this.minTime) / 1000, // seconds
        timeRange: {
          start: this.minTime,
          end: this.maxTime
        }
      };

    } catch (error) {
      console.error('❌ ReplayManager: Failed to load replay data:', error);
      throw error;
    }
  }

  /**
   * Load directly from a parsed data object (e.g. from IGC files)
   * @param {object} data - Parsed flight data directly generated in JS
   */
  loadData(data) {
    this.resetAlignStarts();

    // Process flights (already formatted like JSON)
    const allFlights = data.flights.map(flight => this.processFlight(flight, 'json'));

    // Deduplicate by cookie (same pilot may have multiple sessions)
    const bestByCookie = new Map();
    for (const flight of allFlights) {
      const cookie = flight.positions?.[0]?.cookie;
      if (!cookie) continue;
      const existing = bestByCookie.get(cookie);
      if (!existing || flight.positions.length > existing.positions.length) {
        bestByCookie.set(cookie, flight);
      }
    }
    this.flights = Array.from(bestByCookie.values());

    // Set time range
    this.minTime = new Date(data.time_window.start);
    this.maxTime = new Date(data.time_window.end);
    this.currentTime = new Date(this.minTime);

    // Clear existing aircraft from live mode
    this.aircraftManager.clearAll();

    // Fit map to all tracks
    this.fitMapToTracks();

    // Update positions
    this.updatePositions();

    return {
      totalFlights: data.total_flights,
      totalPositions: data.total_positions,
      duration: (this.maxTime - this.minTime) / 1000,
      timeRange: {
        start: this.minTime,
        end: this.maxTime
      }
    };
  }

  /**
   * Process a single flight from API response
   * Handles both JSON (full telemetry) and polyline (compact) formats
   * @param {object} flight - Flight data from API
   * @param {string} format - 'json' or 'polyline'
   */
  processFlight(flight, format = 'json') {
    let coordinates = [];
    let timestamps = [];
    let positions = [];
    const pilotFirstName = flight.pilot?.first_name || '';
    const pilotLastName = flight.pilot?.last_name || '';
    const pilotStructuredName = [pilotFirstName, pilotLastName].filter(Boolean).join(' ').trim();
    const pilotDisplayName = flight._soaringSpotPilotName || flight.pilot?.name || pilotStructuredName || '';

    if (format === 'polyline') {
      // POLYLINE FORMAT: decode polyline string
      if (!flight.polyline || !flight.timestamps) {
        console.warn(`⚠️ Flight ${flight.flight_id} missing polyline or timestamps`);
        coordinates = [];
        timestamps = [];
      } else {
        // Decode polyline to coordinates
        coordinates = decodePolyline(flight.polyline);
        timestamps = flight.timestamps.map(t => new Date(t));

        // DEBUG: Log first flight
        // if (flight.flight_id <= 2) {
        //   console.log(`🔍 DEBUG Flight ${flight.flight_id} (polyline):`, {
        //     polyline_length: flight.polyline.length,
        //     coordinates_count: coordinates.length,
        //     timestamps_count: timestamps.length,
        //     first_coord: coordinates[0],
        //     first_timestamp: timestamps[0]
        //   });
        // }

        // Create position objects (limited data - no telemetry)
        positions = coordinates.map((coord, i) => ({
          lat: coord[0],
          lon: coord[1],
          timestamp: timestamps[i],
          cookie: cookieRegistry.registerFlight(flight.flight_id, flight.pilot.callsign),
          callsign: flight.pilot.callsign,
          id_cn: flight.pilot.callsign,
          id_fname: pilotFirstName || pilotDisplayName,
          id_lname: pilotLastName || '',
          aircraft_type: flight.aircraft.model_name,
          serverName: flight.server.name,
          portNumber: flight.server.id.toString(),
          // No telemetry data in polyline format
          altitude_m: 0,
          speed_mps: 0,
          vario_mps: 0,
          heading_deg: 0
        }));
      }
    } else {
      // JSON FORMAT: full position data with telemetry
      const rawPositions = flight.positions || [];

      // DEBUG: Log first position to see structure
      // if (rawPositions.length > 0 && flight.flight_id <= 2) {
      //   console.log(`🔍 DEBUG Flight ${flight.flight_id} (JSON):`, {
      //     raw_positions_count: rawPositions.length,
      //     first_position: rawPositions[0],
      //     position_keys: Object.keys(rawPositions[0] || {})
      //   });
      // }

      // Filter out invalid positions (missing lat/lon)
      // API uses 'latitude'/'longitude' (strings), convert to numbers
      const validPositions = rawPositions.filter(p => {
        const lat = parseFloat(p.latitude || p.lat);
        const lon = parseFloat(p.longitude || p.lon);
        return !isNaN(lat) && !isNaN(lon);
      });

      if (validPositions.length === 0 && rawPositions.length > 0) {
        console.warn(`⚠️ Flight ${flight.flight_id} has no valid positions (raw count: ${rawPositions.length})`);
        // DEBUG: Show why first position was filtered
        const p = rawPositions[0];
        console.log('  First position debug:', {
          has_lat: p.lat !== undefined,
          has_lon: p.lon !== undefined,
          lat_value: p.lat,
          lon_value: p.lon,
          lat_is_nan: isNaN(p.lat),
          lon_is_nan: isNaN(p.lon)
        });
      }

      const filteredPositionResult = positionNoiseFilter.filterSequence(validPositions, {
        continuityKey: `replay_${flight.flight_id}`,
      });
      const filteredPositions = filteredPositionResult.positions;
      if (filteredPositionResult.removed > 0) {
        console.warn(
          `[NoiseFilter] Removed ${filteredPositionResult.removed} impossible-jump sample(s) `
          + `from replay flight ${flight.flight_id} (${flight.pilot?.callsign || 'unknown'})`
        );
      }

      // Parse timestamps and create coordinate array
      // Convert string values to numbers
      timestamps = filteredPositions.map(p => new Date(p.timestamp));
      coordinates = filteredPositions.map(p => [
        parseFloat(p.latitude || p.lat),
        parseFloat(p.longitude || p.lon)
      ]);

      // Create position objects with FULL telemetry data
      positions = filteredPositions.map(pos => ({
        lat: parseFloat(pos.latitude || pos.lat),
        lon: parseFloat(pos.longitude || pos.lon),
        timestamp: new Date(pos.timestamp),
        cookie: cookieRegistry.registerFlight(flight.flight_id, flight.pilot.callsign),
        callsign: flight.pilot.callsign,
        id_cn: flight.pilot.callsign,
        id_fname: pos.id_fname || pilotFirstName || pilotDisplayName,
        id_lname: pos.id_lname || pilotLastName || '',
        aircraft_type: flight.aircraft.model_name,
        serverName: flight.server.name,
        portNumber: flight.server.id.toString(),
        // FULL TELEMETRY DATA for coloring and labels (parse strings to numbers)
        altitude_m: parseFloat(pos.altitude_m) || 0,
        speed_mps: parseFloat(pos.speed_mps) || 0,
        vario_mps: parseFloat(pos.vario_mps) || 0,
        heading_deg: parseFloat(pos.heading_deg) || 0,
        altitude_agl_m: parseFloat(pos.altitude_agl_m) || undefined,
        pitch_deg: parseFloat(pos.pitch_deg) || undefined,
        roll_deg: parseFloat(pos.roll_deg) || undefined,
        yaw_deg: parseFloat(pos.yaw_deg) || undefined
      }));
    }

    return {
      flightId: flight.flight_id,
      pilot: {
        ...flight.pilot,
        name: flight.pilot?.name || pilotStructuredName || null
      },
      aircraft: flight.aircraft,
      server: flight.server,
      coordinates,
      timestamps,
      positions,
      startTime: new Date(flight.time_range.start),
      endTime: new Date(flight.time_range.end),
      currentIndex: 0,
      lastRenderedIndex: -1, // Track last rendered position to avoid redundant updates
      isActive: false,
      metadata: flight.metadata
    };
  }

  /**
   * Draw all flight paths on the map (faded)
   */
  drawFlightPaths() {
    // TODO: Draw polylines on map
    // For now, we'll let the aircraft manager handle trails
    // DEBUG:     console.log('🎬 ReplayManager: Drawing flight paths for', this.flights.length, 'flights');
  }

  /**
   * Fit map to show all tracks
   */
  fitMapToTracks() {
    const allCoords = this.flights.flatMap(f => f.coordinates);
    if (allCoords.length > 0) {
      this.aircraftManager.fitBoundsToCoords(allCoords);
    }
  }

  /**
   * Start playback
   */
  play() {
    if (this.isPlaying) {
      this.isPaused = false;
      if (this._bgWorker) this._bgWorker.postMessage('start');
      this.ensurePlaybackLoop();
      return;
    }

    this.isPlaying = true;
    this.isPaused = false;
    this.lastFrameTime = performance.now();

    // Start background worker timer (keeps replay advancing when tab is hidden)
    if (this._bgWorker) this._bgWorker.postMessage('start');

    // DEBUG:     console.log('▶️ ReplayManager: Starting playback');
    this.ensurePlaybackLoop();
  }

  /**
   * Pause playback
   */
  pause() {
    this.isPlaying = false;
    this.isPaused = true;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Stop background worker timer
    if (this._bgWorker) this._bgWorker.postMessage('stop');

    // DEBUG:     console.log('⏸️ ReplayManager: Paused');
  }

  /**
   * Stop playback and reset
   */
  stop() {
    this.pause();
    this.currentTime = new Date(this.minTime);
    this.aircraftManager.clearAll();
    // DEBUG:     console.log('⏹️ ReplayManager: Stopped');
  }

  /**
   * Set playback speed
   */
  setSpeed(speed) {
    this.playbackSpeed = speed;
    // DEBUG:     console.log(`⏩ ReplayManager: Speed set to ${speed}x`);
  }

  ensurePlaybackLoop() {
    if (!this.isPlaying) return;

    this.lastFrameTime = performance.now();

    if (document.hidden) {
      return;
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => this.animate());
  }

  /**
   * Seek to specific time
   * Uses instant event reconstruction for racing tasks and full history rebuild for AAT
   */
  seekTo(timestamp) {
    const newTime = new Date(timestamp);

    // Reset all cursors when seeking (forward or backward)
    this.flights.forEach(flight => {
      flight.currentIndex = 0;
      flight.lastRenderedIndex = -1; // Force re-render after seek
    });

    // Clear all trails to prevent showing "future" trail data when seeking backwards
    // Trails will be rebuilt as playback continues from the new position
    if (this.mapManager && typeof this.mapManager.clearAllTrails === 'function') {
      this.mapManager.clearAllTrails();
    }

    // Reconstruct aircraft state from precomputed events (FAST - O(n*m) where n=flights, m=TPs)
    // This is instant because we already analyzed the entire track on load
    if (this.aircraftStateTracker && this.selectedTask) {
      this.rebuildAircraftStates(newTime);
    }

    this.currentTime = newTime;
    this.updatePositions();
  }

  /**
   * Animation loop
   */
  animate() {
    if (!this.isPlaying) return;
    this.animationFrameId = null;

    const now = performance.now();
    const deltaMs = now - this.lastFrameTime;

    // Advance time based on playback speed
    const timeAdvance = deltaMs * this.playbackSpeed;
    const newTime = this.currentTime.getTime() + timeAdvance;
    this.currentTime = new Date(newTime);

    // In live mode at live edge: clamp to liveEdge and wait for more data
    if (this.isLiveMode && this.liveEdge) {
      const edgeMs = this.liveEdge.getTime();
      if (newTime >= edgeMs) {
        this.currentTime = new Date(edgeMs);
        this.updatePositions();
        this.lastFrameTime = now;
        if (!document.hidden) {
          this.animationFrameId = requestAnimationFrame(() => this.animate());
        }
        return;
      }
    }

    // Check if we've reached the end (only pause in non-live mode)
    const effectiveMaxTime = this.getEffectiveMaxTime();
    if (this.currentTime >= effectiveMaxTime) {
      this.currentTime = new Date(effectiveMaxTime);
      this.updatePositions();
      if (!this.isLiveMode) {
        this.pause();
        // DEBUG:       console.log('✅ ReplayManager: Playback complete');
      }
      return;
    }

    // Update positions
    this.updatePositions();

    this.lastFrameTime = now;

    // Use rAF when tab is visible (smooth 60fps); when hidden, the worker tick calls animate()
    if (!document.hidden) {
      this.animationFrameId = requestAnimationFrame(() => this.animate());
    }
  }

  /**
   * Set callback for when positions are updated (for widgets/UI)
   */
  setPositionUpdateCallback(callback) {
    this.positionUpdateCallback = callback;
  }

  /**
   * Update all aircraft positions for current time
   * Renders interpolated positions every frame for smooth movement,
   * but only performs expensive calculations (TEC, TP detection) when the 1Hz sample index changes.
   * Trail points are only added at 1Hz to maintain consistent trail length regardless of playback speed.
   */
  updatePositions() {
    const positionsToRender = [];
    const trailPositionsToAdd = []; // Only add trail points at 1Hz, not every frame
    let didAdvanceSample = false;
    let didActivityChange = false;
    const shouldPreferPrecomputedState =
      !!this.aircraftStateTracker &&
      !!this.selectedTask &&
      !this.shouldRebuildStateFromHistory(this.selectedTask) &&
      (!this.isLiveMode || !this.isAtLiveEdge());

    this.flights.forEach((flight, flightIndex) => {
      // Get per-flight effective time (translated when Align Starts is active)
      const effectiveTime = this.getFlightTime(flightIndex);

      // Check if flight is active at effective time
      if (effectiveTime < flight.startTime || effectiveTime > flight.endTime) {
        if (flight.isActive) {
          // Remove aircraft from both 2D and 3D (including trails)
          const cookie = flight.positions[0].cookie;
          this.aircraftManager.removeAircraft(cookie);
          this.mapManager.removeAircraft(cookie);
          flight.isActive = false;
          didActivityChange = true;
        }
        return;
      }

      // Find interpolated position at effective time
      const position = this.getPositionAtTime(flight, effectiveTime);
      if (!position) return;

      // Check if we've moved to a new 1Hz sample (for expensive calculations and trail updates)
      const indexChanged = flight.lastRenderedIndex !== flight.currentIndex;

      if (indexChanged) {
        didAdvanceSample = true;

        // Phase 4: Calculate TEC altitude and vario for replay aircraft (with lazy loading)
        // Only do this when sample changes to avoid excessive computation
        if (this.polarManager && this.energyCalculator && position.aircraft_type) {
          const gliderType = position.aircraft_type;

          // Try to load polar on-demand if we don't have it yet (non-blocking)
          if (!this.polarManager.hasPolar(gliderType)) {
            this.polarManager.loadPolarOnDemand(gliderType).catch(err => {
              // Silently fail - polar not available
            });
          } else {
            // We have the polar, calculate TEC
            const altitude_m = parseFloat(position.altitude_m);
            const speed_kmh = parseFloat(position.speed_mps) * 3.6; // Convert to km/h

            if (!isNaN(altitude_m) && !isNaN(speed_kmh)) {
              const timestamp = position.timestamp.getTime();
              const tecData = this.energyCalculator.updateTEC(position.cookie, altitude_m, speed_kmh, timestamp);
              // Store TEC data on position for display
              position.tec_m = tecData.tec_m;
              position.tec_vario_ms = tecData.vario_ms;
              position.kinetic_m = tecData.kinetic_m;
            }
          }
        }

        // Phase 2 & 3: Start/Finish/TP detection and distance calculation if task is selected
        // Only check when sample changes for efficiency
        if (this.aircraftStateTracker && this.selectedTask && this.selectedTask.turnpoints && this.selectedTask.turnpoints.length > 0) {
          const hasPrecomputedEvents = this.aircraftStateTracker.precomputedEvents?.has(position.cookie);

          if (shouldPreferPrecomputedState && hasPrecomputedEvents) {
            // Replay history already knows the authoritative start/TP/finish events.
            // Reuse that state while replaying history so playback matches scrubbing.
            this.aircraftStateTracker.reconstructStateFromEvents(
              position.cookie,
              effectiveTime,
              this.selectedTask,
              position
            );
          } else {
            const startSector = this.selectedTask.turnpoints[0]; // First TP is start
            const finishSector = this.selectedTask.turnpoints[this.selectedTask.turnpoints.length - 1]; // Last TP is finish

            // Get max start altitude from task (use 999999m if not specified)
            const maxStartAltitude_m = this.selectedTask.max_start_altitude_m || 999999;
            const rawStartGateTime = this.selectedTask.start_time_override_iso || this.selectedTask.startTimeOverrideIso || null;
            const startGateTimeMs = rawStartGateTime ? new Date(rawStartGateTime).getTime() : null;

            // Check for start crossing (leaving start sector)
            this.aircraftStateTracker.checkStartCrossing(
              position,
              startSector,
              maxStartAltitude_m,
              startGateTimeMs,
              this.selectedTask,
              { requireDirectionTowardNextTp: this.isLiveMode === true }
            );

            // Phase 3: Check for turnpoint touches
            this.aircraftStateTracker.checkTurnpointTouch(position, this.selectedTask);

            // Phase 3: Calculate task distance
            this.aircraftStateTracker.calculateDistance(position, this.selectedTask);

            // Check for finish crossing (entering finish sector) - Phase 3 update: requires all TPs
            this.aircraftStateTracker.checkFinishCrossing(position, finishSector, this.selectedTask);

            // Update elapsed time for racing aircraft
            this.aircraftStateTracker.updateElapsedTime(position.cookie, position.timestamp);
          }
        }

        // Store enriched data for carrying forward to interpolated frames
        flight.lastEnrichedData = {
          tec_m: position.tec_m,
          tec_vario_ms: position.tec_vario_ms,
          kinetic_m: position.kinetic_m
        };

        flight.lastRenderedIndex = flight.currentIndex;

        // Mark this position for trail update (only at 1Hz sample boundaries)
        trailPositionsToAdd.push({ flight, position });
      } else {
        // Carry forward enriched data from last 1Hz sample to interpolated positions
        // This prevents label/color flashing during smooth movement
        if (flight.lastEnrichedData) {
          position.tec_m = flight.lastEnrichedData.tec_m;
          position.tec_vario_ms = flight.lastEnrichedData.tec_vario_ms;
          position.kinetic_m = flight.lastEnrichedData.kinetic_m;
        }
      }

      // Always queue the interpolated position for rendering (smooth movement)
      positionsToRender.push(position);
      flight.isActive = true;
    });

    // Widgets only need replay-side recomputation when the authoritative 1Hz sample changes
    // or when aircraft become active/inactive. Calling this every animation frame causes
    // heavy leaderboard/UI work to compete with interpolation and makes playback stutter.
    if (this.positionUpdateCallback && (didAdvanceSample || didActivityChange)) {
      try {
        this.positionUpdateCallback();
      } catch (error) {
        console.error('Replay position update callback failed:', error);
      }
    }

    // Render all interpolated aircraft positions for smooth marker movement
    // Only updates position/rotation, not trails/labels/colors
    positionsToRender.forEach(position => {
      // Update 2D aircraft marker position (smooth movement)
      this.aircraftManager.updateAircraftMarkerOnly(position);
      // Update 3D aircraft marker position (smooth movement)
      this.mapManager.updateAircraftMarkerOnly(position);
    });

    // Update trails at 1Hz sample boundaries (not every frame).
    // Label throttling is handled inside AircraftManager.updateAircraft() itself.
    trailPositionsToAdd.forEach(({ position }) => {
      this.aircraftManager.updateAircraft(position);
      this.mapManager.updateAircraft(position);
    });
  }

  /**
   * Get position at specific time using cursor-based lookup with interpolation
   * Advances the cursor only when time moves forward (O(1) amortized)
   * Interpolates position between samples for smooth playback and accurate TP detection
   */
  getPositionAtTime(flight, targetTime) {
    const timestamps = flight.timestamps;
    const positions = flight.positions;

    if (positions.length === 0) return null;

    // Advance cursor while next timestamp is still before target time
    // This is O(1) amortized because we only advance, never go back
    while (flight.currentIndex < timestamps.length - 1 &&
      timestamps[flight.currentIndex + 1] <= targetTime) {
      flight.currentIndex++;
    }

    const currentPos = positions[flight.currentIndex];

    // If we're at the last sample or exactly on a timestamp, return the sample
    if (flight.currentIndex >= timestamps.length - 1) {
      return currentPos;
    }

    const nextPos = positions[flight.currentIndex + 1];
    const currentTime = timestamps[flight.currentIndex].getTime();
    const nextTime = timestamps[flight.currentIndex + 1].getTime();
    const targetTimeMs = targetTime.getTime();

    // If exactly on a timestamp, return the sample
    if (targetTimeMs === currentTime) {
      return currentPos;
    }

    // Interpolate between current and next position
    const timeDelta = nextTime - currentTime;
    const timeOffset = targetTimeMs - currentTime;
    const t = timeDelta > 0 ? timeOffset / timeDelta : 0; // Fraction between samples (0-1)

    // Reuse a per-flight mutable object to avoid allocation every frame.
    // Copy static properties only when the base sample changes.
    let interpolated = flight._interpolated;
    if (!interpolated || flight._interpolatedBaseIdx !== flight.currentIndex) {
      // Base sample changed — shallow copy all static properties once
      interpolated = Object.assign(flight._interpolated || {}, currentPos);
      flight._interpolated = interpolated;
      flight._interpolatedBaseIdx = flight.currentIndex;
    }

    // Update only the interpolated numeric fields each frame
    interpolated.lat = currentPos.lat + (nextPos.lat - currentPos.lat) * t;
    interpolated.lon = currentPos.lon + (nextPos.lon - currentPos.lon) * t;
    interpolated.altitude_m = currentPos.altitude_m + (nextPos.altitude_m - currentPos.altitude_m) * t;
    interpolated.speed_mps = currentPos.speed_mps + (nextPos.speed_mps - currentPos.speed_mps) * t;
    interpolated.vario_mps = currentPos.vario_mps + (nextPos.vario_mps - currentPos.vario_mps) * t;
    interpolated.heading_deg = this.interpolateHeading(currentPos.heading_deg, nextPos.heading_deg, t);
    // Lazily set timestamp only once per ms value to avoid new Date() every frame
    if (!interpolated._tsMs || interpolated._tsMs !== targetTimeMs) {
      interpolated.timestamp = new Date(targetTimeMs);
      interpolated._tsMs = targetTimeMs;
    }

    // Interpolate optional fields if they exist
    if (currentPos.altitude_agl_m !== undefined && nextPos.altitude_agl_m !== undefined) {
      interpolated.altitude_agl_m = currentPos.altitude_agl_m + (nextPos.altitude_agl_m - currentPos.altitude_agl_m) * t;
    }
    if (currentPos.pitch_deg !== undefined && nextPos.pitch_deg !== undefined) {
      interpolated.pitch_deg = currentPos.pitch_deg + (nextPos.pitch_deg - currentPos.pitch_deg) * t;
    }
    if (currentPos.roll_deg !== undefined && nextPos.roll_deg !== undefined) {
      interpolated.roll_deg = currentPos.roll_deg + (nextPos.roll_deg - currentPos.roll_deg) * t;
    }
    if (currentPos.yaw_deg !== undefined && nextPos.yaw_deg !== undefined) {
      interpolated.yaw_deg = this.interpolateHeading(currentPos.yaw_deg, nextPos.yaw_deg, t);
    }

    return interpolated;
  }

  /**
   * Interpolate heading/yaw angles correctly (handles 0-360 wraparound)
   * @param {number} angle1 - Start angle in degrees (0-360)
   * @param {number} angle2 - End angle in degrees (0-360)
   * @param {number} t - Interpolation factor (0-1)
   * @returns {number} Interpolated angle in degrees (0-360)
   */
  interpolateHeading(angle1, angle2, t) {
    // Normalize angles to 0-360
    angle1 = ((angle1 % 360) + 360) % 360;
    angle2 = ((angle2 % 360) + 360) % 360;

    // Find shortest path between angles
    let delta = angle2 - angle1;
    if (delta > 180) {
      delta -= 360;
    } else if (delta < -180) {
      delta += 360;
    }

    // Interpolate and normalize result
    const result = angle1 + delta * t;
    return ((result % 360) + 360) % 360;
  }

  /**
   * Get current playback progress (0-1)
   */
  getProgress() {
    const min = this.getEffectiveMinTime();
    const max = this.getEffectiveMaxTime();
    if (!min || !max) return 0;
    const total = max - min;
    const current = this.currentTime - min;
    return Math.max(0, Math.min(1, current / total));
  }

  /**
   * Get current time as seconds from start
   */
  getCurrentTimeSeconds() {
    const min = this.getEffectiveMinTime();
    if (!min) return 0;
    return (this.currentTime - min) / 1000;
  }

  /**
   * Get total duration in seconds
   */
  getDurationSeconds() {
    const min = this.getEffectiveMinTime();
    const max = this.getEffectiveMaxTime();
    if (!min || !max) return 0;
    return (max - min) / 1000;
  }

  // ── Live-append (PVR) methods ──────────────────────────────────────

  /**
   * Initialize live replay mode after historical data is loaded.
   * Builds the cookie→flight lookup index and sets mode flags.
   */
  initLiveReplay() {
    this.isLiveMode = true;
    this.cookieToFlightIndex.clear();

    // Build index from existing flights
    this.flights.forEach((flight, idx) => {
      if (flight.positions.length > 0) {
        const cookie = flight.positions[0].cookie;
        this.cookieToFlightIndex.set(cookie, idx);
      }
    });

    // Set live edge to current maxTime (end of historical data)
    this.liveEdge = this.maxTime ? new Date(this.maxTime) : new Date();

    console.log(`🔴 Live replay mode initialized: ${this.flights.length} flights, edge at ${this.liveEdge.toISOString()}`);
  }

  /**
   * Append a normalized live position to the appropriate flight.
   * Creates a new flight entry if the cookie is not yet known.
   * @param {Object} pos - normalized position (from positionNormalizer)
   */
  appendLivePosition(pos) {
    if (!this.isLiveMode) return;

    const cookie = pos.cookie;
    if (!cookie) return;
    const pilotDisplayName =
      [pos.id_fname, pos.id_lname].filter(Boolean).join(' ').trim() ||
      pos.pilotName ||
      pos.pilot_name ||
      null;

    // Ensure timestamp is a Date
    const timestamp = pos.timestamp instanceof Date ? pos.timestamp : new Date(pos.timestamp);

    let flightIdx = this.cookieToFlightIndex.get(cookie);

    if (flightIdx === undefined) {
      // New pilot — create a flight entry
      const newFlight = {
        flightId: null,
        pilot: { callsign: pos.callsign || pos.id_cn || '', name: pilotDisplayName },
        aircraft: { model_name: pos.aircraft_type || '' },
        server: { name: pos.serverName || '', id: pos.portNumber || '' },
        coordinates: [],
        timestamps: [],
        positions: [],
        startTime: timestamp,
        endTime: timestamp,
        currentIndex: 0,
        lastRenderedIndex: -1,
        isActive: false,
        metadata: null
      };
      flightIdx = this.flights.length;
      this.flights.push(newFlight);
      this.cookieToFlightIndex.set(cookie, flightIdx);
    }

    const flight = this.flights[flightIdx];
    if (!flight.pilot?.name && pilotDisplayName) {
      if (!flight.pilot) {
        flight.pilot = { callsign: pos.callsign || pos.id_cn || '' };
      }
      flight.pilot.name = pilotDisplayName;
    }

    // Append the position (sorted — live data arrives in order)
    flight.positions.push(pos);
    flight.timestamps.push(timestamp);
    flight.coordinates.push([pos.lat, pos.lon]);

    // Extend flight time range
    if (timestamp > flight.endTime) {
      flight.endTime = timestamp;
    }

    // Extend global time range
    const tsMs = timestamp.getTime();
    if (!this.maxTime || tsMs > this.maxTime.getTime()) {
      this.maxTime = new Date(tsMs);
    }

    // Update live edge
    if (!this.liveEdge || tsMs > this.liveEdge.getTime()) {
      this.liveEdge = new Date(tsMs);
    }

    // Mark flight for deferred event recomputation
    this._pendingEventRecompute.add(cookie);

    // Periodically recompute events for flights that received new data
    const now = performance.now();
    if (now - this._lastEventRecomputeTime > 10000) { // Every 10 seconds
      this._recomputePendingEvents();
      this._lastEventRecomputeTime = now;
    }
  }

  /**
   * Check if playback cursor is at or near the live edge.
   * @param {number} [toleranceMs=2000] - how close counts as "at edge"
   * @returns {boolean}
   */
  isAtLiveEdge(toleranceMs = 2000) {
    if (!this.isLiveMode || !this.liveEdge || !this.currentTime) return false;
    return (this.liveEdge.getTime() - this.currentTime.getTime()) <= toleranceMs;
  }

  /**
   * Jump playback cursor to the live edge.
   */
  jumpToLiveEdge() {
    if (!this.liveEdge) return;
    this.seekTo(this.liveEdge);
    if (!this.isPlaying) {
      this.play();
    } else {
      this.ensurePlaybackLoop();
    }
  }

  /**
   * Exit live mode (e.g., when task is deselected).
   */
  exitLiveMode() {
    this.isLiveMode = false;
    this.liveEdge = null;
    this.cookieToFlightIndex.clear();
    this._pendingEventRecompute.clear();
  }

  /**
   * Recompute task events for flights that received new live positions.
   * Runs precomputeEvents on individual flights rather than all flights.
   * @private
   */
  _recomputePendingEvents() {
    if (!this.aircraftStateTracker || !this.selectedTask || this._pendingEventRecompute.size === 0) {
      this._pendingEventRecompute.clear();
      return;
    }

    const maxStartAltitude_m = this.selectedTask.max_start_altitude_m || 999999;

    for (const cookie of this._pendingEventRecompute) {
      const idx = this.cookieToFlightIndex.get(cookie);
      if (idx === undefined) continue;
      const flight = this.flights[idx];
      if (!flight || flight.positions.length === 0) continue;

      // Re-run precomputeEvents for this single flight
      this.aircraftStateTracker.precomputeEvents(
        cookie,
        flight.positions,
        this.selectedTask,
        maxStartAltitude_m
      );
    }

    this._pendingEventRecompute.clear();
  }

  /**
   * Clean up
   */
  destroy() {
    this.exitLiveMode();
    this.stop();
    this.aircraftManager.clearAll();
    this.flights = [];

    // Clean up worker and revoke blob URL
    if (this._bgWorker) {
      this._bgWorker.terminate();
      this._bgWorker = null;
    }
    if (this._bgWorkerUrl) {
      URL.revokeObjectURL(this._bgWorkerUrl);
      this._bgWorkerUrl = null;
    }
  }
}
